/**
 * AdjuntosSection · seccion ADJUNTOS del ActionCenterDetailPanel.
 *
 * 0674 F3 piloto multi-modelo · BACKLOG [0666b][DETAIL-ATTACHMENTS].
 * Reemplaza el placeholder cross-artefacto. Maneja upload (drag-drop nativo
 * HTML5), lista, descarga (signed URL 5min) y eliminacion soft-delete.
 */

import { useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { useI18n } from '../../../../i18n';
import { PersonaChip } from '../../../shared/components/PersonaChip';
import type { ArtefactoWorkflowTipo } from '../hooks/useActionCenterDetail';
import {
  descargarAdjunto,
  formatTamano,
  useArtefactoAdjuntos,
  useEliminarAdjunto,
  useSubirAdjunto,
} from '../hooks/useArtefactoAdjuntos';
import styles from './AdjuntosSection.module.css';

export interface AdjuntosSectionProps {
  artefactoTipo: ArtefactoWorkflowTipo;
  artefactoId: string;
}

export function AdjuntosSection(props: AdjuntosSectionProps) {
  const { t } = useI18n();
  const tt = t.sign.actionCenter.premium.adjuntos;

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adjuntosQuery = useArtefactoAdjuntos(props.artefactoTipo, props.artefactoId);
  const subirMut = useSubirAdjunto(props.artefactoTipo, props.artefactoId);
  const eliminarMut = useEliminarAdjunto(props.artefactoTipo, props.artefactoId);

  const handleFiles = async (files: FileList | File[]) => {
    setError(null);
    const arr = Array.from(files);
    for (const file of arr) {
      try {
        await subirMut.mutateAsync(file);
      } catch (err) {
        const msg = err instanceof Error ? err.message : tt.errorSubida;
        setError(msg);
        break;
      }
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      void handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDescargar = async (adjuntoId: string) => {
    setError(null);
    try {
      const { url } = await descargarAdjunto(adjuntoId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg = err instanceof Error ? err.message : tt.errorDescarga;
      setError(msg);
    }
  };

  const handleEliminar = async (adjuntoId: string) => {
    if (!window.confirm(tt.confirmarEliminar)) return;
    setError(null);
    try {
      await eliminarMut.mutateAsync(adjuntoId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : tt.errorEliminacion;
      setError(msg);
    }
  };

  const adjuntos = adjuntosQuery.data ?? [];

  return (
    <div className={styles.container}>
      <div
        className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ''}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
        aria-label={tt.dropzonePrompt}
      >
        <svg
          className={styles.dropzoneIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <p className={styles.dropzonePrompt}>{tt.dropzonePrompt}</p>
        <p className={styles.dropzoneHint}>{tt.dropzoneHint}</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className={styles.hiddenInput}
          onChange={handleInputChange}
        />
      </div>

      {subirMut.isPending && <div className={styles.subiendo}>{tt.subiendo}</div>}
      {error && <div className={styles.error}>{error}</div>}

      {adjuntos.length === 0 && !adjuntosQuery.isLoading ? (
        <p className={styles.vacio}>{tt.listaVacia}</p>
      ) : (
        <ul className={styles.lista}>
          {adjuntos.map((adj) => (
            <li key={adj.id} className={styles.item}>
              <svg
                className={styles.itemIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div className={styles.itemMeta}>
                <p className={styles.itemNombre}>{adj.nombreArchivo}</p>
                <p className={styles.itemInfo}>
                  {formatTamano(adj.tamanoBytes)} · {new Date(adj.creadoEn).toLocaleDateString()}
                </p>
                {/* 0677: reemplaza UUID corto subidoPor por PersonaChip reusable */}
                <PersonaChip userId={adj.subidoPor} />
              </div>
              <div className={styles.itemAcciones}>
                <button
                  type="button"
                  className={styles.btnIcon}
                  onClick={() => void handleDescargar(adj.id)}
                  title={tt.descargar}
                  aria-label={tt.descargar}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`${styles.btnIcon} ${styles.btnIconDanger}`}
                  onClick={() => void handleEliminar(adj.id)}
                  disabled={eliminarMut.isPending}
                  title={tt.eliminar}
                  aria-label={tt.eliminar}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
